import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useSiteLogo } from '../hooks/useSiteLogo';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const COLORED_LOGO = useSiteLogo();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.first_name}!`);
      if (user.role === 'admin' || user.role === 'hr_manager') {
        navigate('/admin');
      } else {
        navigate('/intranet');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-neutral-200">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={COLORED_LOGO} alt="NSEC" className="h-16 mx-auto mb-6" />
            </Link>
            <h1 className="font-outfit font-bold text-2xl text-neutral-900">Employee Login</h1>
            <p className="text-neutral-600 mt-2">Sign in to access the intranet</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-neutral-50 border-neutral-200"
                placeholder="your.email@nsecltd.co.uk"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-neutral-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-neutral-50 border-neutral-200 pr-10"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-lime-600 hover:bg-lime-700 text-white font-semibold py-6 rounded-full"
              data-testid="login-submit"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-600 text-sm">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-lime-600 hover:underline font-medium">
                Register
              </Link>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-neutral-200 text-center">
            <Link to="/" className="text-neutral-500 text-sm hover:text-neutral-700">
              ← Back to main website
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
